import type {
  PostCompanyReviewExperiment2Input,
  PostCompanyReviewInput,
} from '@seek/ca-graphql-schema/types';
import { ORPHAN_REVIEW_COMPANY_ID } from '@seek/libs-shared';
import type { Language } from '@seek/melways-sites';

import type { ReviewPayload } from '../../type';

const convertRating = (
  rating: string | number | undefined | null,
): number | null => (rating ? Number(rating) : null);

export const mapReview = (
  language: Language,
  review: ReviewPayload,
): PostCompanyReviewInput =>
  ({
    companyId: review.companyId ?? ORPHAN_REVIEW_COMPANY_ID,
    companyName: review.companyName,
    organisationId: review.organisationId,
    languageISO: language,
    details: {
      overallExperienceDetails: review.overallExperienceDetails,
      cons: review.theChallenges,
      pros: review.theGoodThings,
      isCompanyRecommended: review.isRecommended ?? false,
      overallRating: Number(review.overallRating),
      overallExperience: review.title,
    },
    job: {
      history: {
        from: {
          year: review.workHistoryFrom?.year,
          month: review.workHistoryFrom?.month,
        },
        ...(!review.isStillWorkHere && review.workHistoryTo
          ? {
              to: {
                year: review.workHistoryTo.year,
                month: review.workHistoryTo.month,
              },
            }
          : {}),
      },
      isJobTitleAnonymised: false,
      location: {
        fullLocation: review.fullLocation,
        locationId: review.locationId,
      },
      roleTitleId: review.jobTitleId,
      title: review.jobTitleText,
      workType: review.workType,
    },
    salary: {
      includeBonus: review.salary?.includeBonus ?? false,
      rating: review.salaryRating,
      type: review.salaryType,
      minimum: review.salary?.minimum,
      maximum: review.salary?.maximum,
      currency: review.salary?.currency,
    },
    ...(review.perksAndBenefitsRating
      ? {
          perksAndBenefits: { rating: Number(review.perksAndBenefitsRating) },
        }
      : {}),
    ...(review.careerOpportunityRating
      ? {
          careerOpportunity: {
            rating: Number(review.careerOpportunityRating),
          },
        }
      : {}),
    ...(review.diversityRating
      ? {
          diversity: {
            rating: Number(review.diversityRating),
          },
        }
      : {}),
    ...(review.executiveManagementRating
      ? {
          executiveManagement: {
            rating: Number(review.executiveManagementRating),
          },
        }
      : {}),
    ...(review.workEnvironmentRating
      ? {
          workEnvironment: {
            rating: Number(review.workEnvironmentRating),
          },
        }
      : {}),
    ...(review.workLifeBalanceRating
      ? {
          workLifeBalance: {
            rating: Number(review.workLifeBalanceRating),
          },
        }
      : {}),
  }) as PostCompanyReviewInput;

export const mapReviewExperimentTwo = (
  review: ReviewPayload,
): PostCompanyReviewExperiment2Input => {
  const {
    reviewId = null, // Don't send empty string
    companyName = '',
    companyId = ORPHAN_REVIEW_COMPANY_ID,
    organisationId = null, // Don't send empty string
    zone,
    workLifeBalanceRating,
    workLifeBalanceDescription = '',
    workEnvironmentRating,
    workEnvironmentDescription = '',
    executiveManagementRating,
    executiveManagementDescription = '',
    diversityRating,
    diversityDescription = '',
    careerOpportunityRating,
    careerOpportunityDescription = '',
    perksAndBenefitsRating,
    perksAndBenefitsDescription = '',
    salaryRating = null,
    salaryType = null,
    salary: {
      minimum: salaryMinimum = null,
      maximum: salaryMaximum = null,
      currency: salaryCurrency = null,
      includeBonus: salaryIncludeBonus = null,
    } = {},
    overallRating,
    overallDescription = '',
    isStillWorkHere = false,
    jobTitleId = '',
    genericTitle = '',
    locationId,
    workType = '',
    workHistoryFrom: { year: fromYear = 0, month: fromMonth = 0 } = {},
    workHistoryTo: { year: toYear = 0, month: toMonth = 0 } = {},
  } = review;

  return {
    reviewId,
    companyName,
    companyId,
    organisationId,
    zone,
    workLifeBalance: {
      rating: convertRating(workLifeBalanceRating),
      description: workLifeBalanceDescription,
    },
    workEnvironment: {
      rating: convertRating(workEnvironmentRating),
      description: workEnvironmentDescription,
    },
    executiveManagement: {
      rating: convertRating(executiveManagementRating),
      description: executiveManagementDescription,
    },
    diversity: {
      rating: convertRating(diversityRating),
      description: diversityDescription,
    },
    careerOpportunity: {
      rating: convertRating(careerOpportunityRating),
      description: careerOpportunityDescription,
    },
    perksAndBenefits: {
      rating: convertRating(perksAndBenefitsRating),
      description: perksAndBenefitsDescription,
    },
    salary: {
      rating: salaryRating,
      type: salaryType,
      minimum: salaryMinimum,
      maximum: salaryMaximum,
      currency: salaryCurrency,
      includeBonus: salaryIncludeBonus,
    },
    overall: {
      rating: Number(overallRating),
      description: overallDescription,
    },
    role: {
      jobTitleId,
      locationId: String(locationId),
      genericTitle,
      workType,
      isCurrentEmployee: isStillWorkHere,
      workHistory: {
        from: {
          year: Number(fromYear),
          month: Number(fromMonth),
        },
        ...(!isStillWorkHere && toYear && toMonth
          ? {
              to: {
                year: Number(toYear),
                month: Number(toMonth),
              },
            }
          : {}),
      },
    },
  } as PostCompanyReviewExperiment2Input;
};
